import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { ChevronRight, Film, ImageIcon, Loader2, RefreshCw } from 'lucide-react'
import { api } from '@/lib/api'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import type { FeedbackBatchStats } from '@/types'

/** Image variants 总览：按日期聚合采纳/拒绝/下载数据，点击批次进入标注详情。 */
export function FeedbackOverviewPage() {
  const navigate = useNavigate()

  const overviewQuery = useQuery({
    queryKey: ['feedback-overview'],
    queryFn: () => api.getFeedbackOverview(),
    refetchOnWindowFocus: true,
  })

  // 按日期分组（倒序），日期内保持后端给的 market 顺序
  const dateGroups = useMemo(() => {
    const groups: { date: string; batches: FeedbackBatchStats[] }[] = []
    const byDate = new Map<string, FeedbackBatchStats[]>()
    for (const batch of overviewQuery.data ?? []) {
      const date = batch.date ?? '未知日期'
      let bucket = byDate.get(date)
      if (!bucket) {
        bucket = []
        byDate.set(date, bucket)
        groups.push({ date, batches: bucket })
      }
      bucket.push(batch)
    }
    return groups
  }, [overviewQuery.data])

  const openBatch = (batch: FeedbackBatchStats) => {
    // 批次元数据缺 date/market 时无法定位详情，禁止跳转（否则详情页会
    // 自动落到另一个批次，造成标错对象）
    if (!batch.date || !batch.market) return
    const params = new URLSearchParams({
      date: batch.date,
      market: batch.market,
      type: batch.creative_type ?? 'image',
    })
    navigate(`/finance/feedback/review?${params.toString()}`)
  }

  return (
    <div className="p-6 space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Image variants</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            按日期查看各市场反馈素材的采纳 / 拒绝 / 下载情况，点击批次进入标注页面。
          </p>
        </div>
        <Button
          variant="outline"
          onClick={() => overviewQuery.refetch()}
          disabled={overviewQuery.isFetching}
        >
          <RefreshCw className={cn('h-4 w-4 mr-2', overviewQuery.isFetching && 'animate-spin')} />
          刷新
        </Button>
      </div>

      {overviewQuery.isLoading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : overviewQuery.isError ? (
        <div className="rounded-lg border border-gray-200 bg-white p-8 text-center text-sm text-red-500">
          加载失败
          <button
            type="button"
            className="ml-2 text-blue-600 hover:underline"
            onClick={() => overviewQuery.refetch()}
          >
            重试
          </button>
        </div>
      ) : dateGroups.length === 0 ? (
        <div className="rounded-lg border border-dashed border-gray-300 bg-white p-8 text-center text-sm text-muted-foreground">
          还没有反馈批次数据。
        </div>
      ) : (
        <div className="space-y-4">
          {dateGroups.map((group) => (
            <DateCard key={group.date} date={group.date} batches={group.batches} onOpen={openBatch} />
          ))}
        </div>
      )}
    </div>
  )
}

function DateCard({
  date,
  batches,
  onOpen,
}: {
  date: string
  batches: FeedbackBatchStats[]
  onOpen: (batch: FeedbackBatchStats) => void
}) {
  const totals = batches.reduce(
    (acc, b) => ({
      variants: acc.variants + b.variant_count,
      reviewed: acc.reviewed + b.reviewed,
      adopted: acc.adopted + b.adopted,
      rejected: acc.rejected + b.rejected,
      downloads: acc.downloads + b.downloads,
    }),
    { variants: 0, reviewed: 0, adopted: 0, rejected: 0, downloads: 0 },
  )

  return (
    <div className="rounded-lg border border-gray-200 bg-white">
      <div className="flex flex-wrap items-center gap-x-5 gap-y-1 border-b border-gray-100 px-4 py-3">
        <div className="text-sm font-semibold">{date}</div>
        <div className="text-xs text-muted-foreground">
          {batches.length} 个市场 · 素材 {totals.variants} · 已评审 {totals.reviewed}
        </div>
        <div className="ml-auto flex items-center gap-4 text-sm">
          <span className="text-emerald-600">采纳 {totals.adopted}</span>
          <span className="text-red-600">拒绝 {totals.rejected}</span>
          <span className="text-blue-600">下载 {totals.downloads}</span>
        </div>
      </div>
      <table className="w-full text-sm">
        <thead className="bg-gray-50 text-xs text-gray-500">
          <tr>
            <th className="px-4 py-2 text-left">市场</th>
            <th className="px-4 py-2 text-left">类型</th>
            <th className="px-4 py-2 text-right">素材数</th>
            <th className="px-4 py-2 text-right">已评审</th>
            <th className="px-4 py-2 text-right">采纳</th>
            <th className="px-4 py-2 text-right">拒绝</th>
            <th className="px-4 py-2 text-right">下载</th>
            <th className="px-4 py-2" />
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-50">
          {batches.map((batch) => {
            const openable = Boolean(batch.date && batch.market)
            return (
            <tr
              key={batch.requirement_id}
              className={cn(openable ? 'cursor-pointer hover:bg-gray-50' : 'opacity-60')}
              title={openable ? undefined : '批次缺少日期/市场元数据，无法打开详情'}
              onClick={() => onOpen(batch)}
            >
              <td className="px-4 py-2 font-medium text-gray-900">{batch.market ?? '-'}</td>
              <td className="px-4 py-2">
                <span className="inline-flex items-center gap-1 text-xs text-gray-500">
                  {batch.creative_type === 'video' ? (
                    <Film className="h-3.5 w-3.5" />
                  ) : (
                    <ImageIcon className="h-3.5 w-3.5" />
                  )}
                  {batch.creative_type ?? 'image'}
                </span>
              </td>
              <td className="px-4 py-2 text-right">{batch.variant_count}</td>
              <td className="px-4 py-2 text-right">{batch.reviewed}</td>
              <td className="px-4 py-2 text-right text-emerald-600">{batch.adopted}</td>
              <td className="px-4 py-2 text-right text-red-600">{batch.rejected}</td>
              <td className="px-4 py-2 text-right text-blue-600">{batch.downloads}</td>
              <td className="px-4 py-2 text-right">
                <ChevronRight className="ml-auto h-4 w-4 text-gray-400" />
              </td>
            </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
